"use client"
import { EditableCell } from "@/components/editable-cell"
import { DropdownCell } from "@/components/dropdown-cell"
import { BooleanCell } from "@/components/boolean-cell"
import { RequestProgressCell } from "@/components/request-progress-cell"
import type { WorkOrder, FieldConfiguration, WorkflowStage } from "@/types/workflow"
import { Avatar, AvatarFallback, getInitials, getAvatarColor } from "@/components/ui/avatar"
import { MultiSelectCell } from "@/components/multi-select-cell"
import { CollectionCell } from "@/components/collection-cell"
import { AlertItemRenderer } from "@/components/alert-item-renderer"
import { useTranslation } from "@/hooks/use-translation"
import { getLocalizedField } from "@/utils/language-utils"
import { useLanguage } from "@/context/language-context"
import { useWorkflow } from '@/context/workflow-context-simple'
import { useEntityType } from "@/contexts/entity-type-context"
import { useCodeValues } from '@/hooks/use-code-values'
import React, { useRef, useLayoutEffect, useEffect, useCallback } from "react"
import { Building2, ChevronDown, Loader2 } from "lucide-react"
import { createPortal } from "react-dom"
import { getDisplayValueFromColumn, isCollectionField, isEmailField } from '@/utils/column-type-helpers'
import { EmailCell } from "@/components/grid/email-cell"
import { formatDateSafely, formatDateOnly, formatDateTime } from '@/utils/format-helpers'
import { useWorkflowApi } from '@/hooks/use-workflow-api'

interface GridCellProps {
  order: WorkOrder
  column: FieldConfiguration
  stage: WorkflowStage
  stageConfig?: any  // Optional: full stage configuration with fieldInteractions
  isEditing: boolean
  onEdit: () => void
  onSave: (value: any) => void
  onCancel: () => void
  fieldNameOverride?: string
  canEditFieldData?: boolean
}

function getCellValue(order: any, columnCode: string): any {
  if (!order || !columnCode) return '';
  const targetKey = columnCode.toLowerCase();
  const foundKey = Object.keys(order).find(key => key.toLowerCase() === targetKey);
  return foundKey !== undefined ? order[foundKey] : '';
}

export function GridCell({ order, column, stage, stageConfig, isEditing, onEdit, onSave, onCancel, fieldNameOverride, canEditFieldData = false }: GridCellProps) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { assignedUsers, state } = useWorkflow();
  const codeValues = useCodeValues();
  const { entityType } = useEntityType();
  const fieldName = fieldNameOverride || column.columnCode;
  const value = getCellValue(order, fieldName);
  // Requests-tab rows in a terminal status are read-only: the backend rejects direct writes on a
  // terminal record (ATS PATCH 409; the VMS / ClientRequest field-update paths guard the same way),
  // so block the inline edit in the UI rather than letting the user attempt a doomed save. Per-entity
  // terminal sets, lower-cased — kept in lock-step with the COMPLETED_STATUSES / VMS_COMPLETED_STATUSES
  // constants and the backend guards. All three entities now share one vocabulary
  // (RequestAccepted / Canceled) after the ATS status alignment. NOTE: VMS 'Rejected' is intentionally
  // NOT terminal — the row is still actionable (operator can restart pre-verification), so it stays
  // editable.
  //
  // ATS keeps its pre-alignment spellings ('accepted' / 'cancelled') in the list as READ tolerance:
  // stored documents carry those values until the Phase 7 data migration rewrites them, and this
  // deploy lands before that migration runs. Drop them only once the migration has run everywhere.
  const terminalStatusesByEntity: Record<string, string[]> = {
    atsplacementrequest: ['requestaccepted', 'canceled', 'accepted', 'cancelled'],
    vmsrequest: ['requestaccepted', 'canceled'],
    clientrequest: ['requestaccepted', 'canceled'],
  };
  const isTerminalRow = !!entityType
    && (terminalStatusesByEntity[entityType.toLowerCase()] ?? [])
        .includes(String(getCellValue(order, 'Status') ?? '').toLowerCase());
  // Cell is editable only if the column allows editing AND the user has the EditFieldData
  // permission AND the row isn't a read-only terminal record.
  const isFieldEditable = column.isEditable && canEditFieldData && !isTerminalRow;
  
  // CRITICAL SAFETY CHECK: If value is an array and column is Alerts, handle it immediately
  if (Array.isArray(value) && (column.columnCode?.toLowerCase() === 'alerts' || fieldName?.toLowerCase() === 'alerts')) {
    // Get field interaction from stage config
    const fieldInteraction = stageConfig?.fieldInteractions?.find(
      (fi: any) => fi.fieldConfigurationId === column.fieldConfigurationId
    );
    
    // Filter and sort by priority (Error > Warning > Information)
    const activeItems = value.filter(item => !item?.isDeleted && !item?.IsDeleted);
    const sortedItems = [...activeItems].sort((a, b) => {
      const priority: Record<string, number> = { 
        Error: 0, 
        error: 0,
        Warning: 1, 
        warning: 1,
        Information: 2,
        information: 2
      };
      const typeA = a.type || a.Type || 'Information';
      const typeB = b.type || b.Type || 'Information';
      return (priority[typeA] ?? 999) - (priority[typeB] ?? 999);
    });
    
    return (
      <CollectionCell
        value={sortedItems}
        column={column}
        fieldInteraction={fieldInteraction}
        renderItem={(item, label) => (
          <AlertItemRenderer item={item} label={label} />
        )}
      />
    );
  }

  // Helper function to get display value for CodeValueSource columns
  const getDisplayValueForCodeValueSource = (cellValue: any, column: FieldConfiguration): string => {
    if (!column.codeValueSource || !codeValues.isReady || !cellValue) {
      return cellValue;
    }
    
    try {
      const displayText = codeValues.getCodeValueTextByCode(String(cellValue), column.codeValueSource);
      return displayText || cellValue; // Fallback to original value if no text found
    } catch (error) {
      return cellValue;
    }
  };

  // --- FIX: Always call useState for search at the top ---
  const [search, setSearch] = React.useState("");
  const cellRef = useRef<HTMLSpanElement>(null);
  const [popoverPos, setPopoverPos] = React.useState<{ top: number; left: number; width: number } | null>(null);
  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Live user search state for AssignedToUser
  const { searchUsers, searchClientOrganizations, searchWorkerOrganizations } = useWorkflowApi();
  const [userSearchResults, setUserSearchResults] = React.useState<Array<{ email: string; fullName: string; profileId?: string }>>([]);
  const [isSearchingUsers, setIsSearchingUsers] = React.useState(false);
  const [userActiveIndex, setUserActiveIndex] = React.useState(-1);
  const userSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const userSearchRequestIdRef = useRef(0);

  // Live client search state for the Client column. Same debounce + in-flight
  // dedupe shape as the user search above — when search results race, we keep
  // only the most recent request's payload (a stale resolve would clobber the
  // new typing's results otherwise).
  type ClientSearchHit = { fboId: number; name: string; obCode: string };
  const [clientSearchResults, setClientSearchResults] = React.useState<ClientSearchHit[]>([]);
  const [isSearchingClients, setIsSearchingClients] = React.useState(false);
  const [clientActiveIndex, setClientActiveIndex] = React.useState(-1);
  const clientSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const clientSearchRequestIdRef = useRef(0);

  // The Client editor below serves both the Client and WorkerOrganization columns —
  // both hold a ClientDto-shaped value ({ fboId, name, obCode }) and commit the same
  // way. Only the backing org search differs: WorkerOrganization is scoped to supplier
  // roles (2/3/4/5), Client to role 1.
  const isWorkerOrgField = (fieldName ?? '').toLowerCase() === 'workerorganization';
  const orgSearchFn = isWorkerOrgField ? searchWorkerOrganizations : searchClientOrganizations;

  const handleClientSearch = useCallback((query: string) => {
    setSearch(query);
    if (clientSearchTimeoutRef.current) clearTimeout(clientSearchTimeoutRef.current);
    clientSearchRequestIdRef.current++;
    if (!query || query.trim().length < 2) {
      setClientSearchResults([]);
      setIsSearchingClients(false);
      return;
    }
    setIsSearchingClients(true);
    clientSearchTimeoutRef.current = setTimeout(async () => {
      const requestId = ++clientSearchRequestIdRef.current;
      try {
        const results = await orgSearchFn(query.trim());
        if (requestId !== clientSearchRequestIdRef.current) return;
        setClientSearchResults(results.map(o => ({
          fboId: o.entityId,
          name: o.displayName,
          obCode: o.organizationCode,
        })));
      } catch {
        if (requestId !== clientSearchRequestIdRef.current) return;
        setClientSearchResults([]);
      } finally {
        if (requestId === clientSearchRequestIdRef.current) setIsSearchingClients(false);
      }
    }, 400);
  }, [orgSearchFn]);

  // Debounced user search — fires API call after 400ms of typing
  const handleUserSearch = useCallback((query: string) => {
    setSearch(query);
    if (userSearchTimeoutRef.current) clearTimeout(userSearchTimeoutRef.current);
    // Invalidate any in-flight request immediately so stale responses are ignored
    userSearchRequestIdRef.current++;
    if (!query || query.trim().length < 2) {
      setUserSearchResults([]);
      setIsSearchingUsers(false);
      return;
    }
    setIsSearchingUsers(true);
    userSearchTimeoutRef.current = setTimeout(async () => {
      const requestId = ++userSearchRequestIdRef.current;
      try {
        const results = await searchUsers(query.trim());
        if (requestId !== userSearchRequestIdRef.current) return;
        setUserSearchResults(results.map(u => ({ email: u.email, fullName: u.fullName || u.email, profileId: u.profileId })));
      } catch {
        if (requestId !== userSearchRequestIdRef.current) return;
        setUserSearchResults([]);
      } finally {
        if (requestId === userSearchRequestIdRef.current) setIsSearchingUsers(false);
      }
    }, 400);
  }, [searchUsers]);

  useLayoutEffect(() => {
    if (isPopoverOpen && cellRef.current) {
      const rect = cellRef.current.getBoundingClientRect();
      setPopoverPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX, width: rect.width });
    }
  }, [isPopoverOpen]);

  useEffect(() => {
    if (!isPopoverOpen) {
      setSearch("");
      setUserSearchResults([]);
      setIsSearchingUsers(false);
      setUserActiveIndex(-1);
      // Cancel any pending debounced search and invalidate in-flight requests
      if (userSearchTimeoutRef.current) { clearTimeout(userSearchTimeoutRef.current); userSearchTimeoutRef.current = null; }
      userSearchRequestIdRef.current++;
      // Same cleanup for the client search side
      setClientSearchResults([]);
      setIsSearchingClients(false);
      setClientActiveIndex(-1);
      if (clientSearchTimeoutRef.current) { clearTimeout(clientSearchTimeoutRef.current); clientSearchTimeoutRef.current = null; }
      clientSearchRequestIdRef.current++;
    }
    if (!isPopoverOpen && isEditing) onCancel();
  }, [isPopoverOpen]);

  useEffect(() => {
    if (!isPopoverOpen) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      // Don't close if clicking inside the cell or the portaled popover
      if (cellRef.current && cellRef.current.contains(target)) return;
      if (popoverRef.current && popoverRef.current.contains(target)) return;
      setIsPopoverOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isPopoverOpen]);

  // Special handling for AssignedToUser column
  if (
    fieldName.toLowerCase() === "assignedtouser" ||
    (typeof value === 'object' && value && (value.email || value.fullName))
  ) {
    // Value can be an object or string (email)
    let email = '';
    if (typeof value === 'object' && value && value.email) {
      email = value.email;
    } else if (typeof value === 'string') {
      email = value;
    }
    const toTitleCase = (str: string) => str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substring(1).toLowerCase());
    const user = assignedUsers?.find(u => u.email?.toLowerCase() === email?.toLowerCase());
    // Prefer: value object fullName → assignedUsers lookup → email fallback
    const valueFullName = typeof value === 'object' && value?.fullName ? value.fullName : '';
    let displayName = '';
    if (valueFullName) {
      displayName = toTitleCase(valueFullName);
    } else if (user?.fullName) {
      displayName = toTitleCase(user.fullName);
    } else {
      displayName = email || '';
    }
    if (isEditing) {
      // Use displayName already computed above (with fullName fallback)
      const selectedLabel = displayName || email || null;
      return (
        <span ref={cellRef} className="relative block w-full" style={{ minHeight: 40 }}>
          <div
            className="absolute left-0 top-0 z-[2002] bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden w-full"
            style={{ minWidth: 220 }}
          >
            {/* Show current selection at the top */}
            {selectedLabel && (
              <div className="flex items-center gap-2.5 px-3 py-2 border-b border-gray-100 bg-gray-50/80">
                <Avatar className="h-6 w-6 flex-shrink-0">
                  <AvatarFallback bgColor={getAvatarColor(selectedLabel)} initials={getInitials(selectedLabel)} />
                </Avatar>
                <span className="text-sm font-medium text-gray-700 truncate">{selectedLabel}</span>
              </div>
            )}
            <div className="p-2 border-b border-gray-100">
              <input
                aria-label={t('searchUsersPlaceholder') || 'Search users'}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:border-gray-300 placeholder:text-muted-foreground"
                placeholder={t('searchUsersPlaceholder') || 'Search users...'}
                value={search}
                onChange={e => { handleUserSearch(e.target.value); setUserActiveIndex(-1); }}
                autoFocus
                onBlur={onCancel}
                onKeyDown={e => {
                  if (e.key === 'ArrowDown') { e.preventDefault(); setUserActiveIndex(i => Math.min(i + 1, userSearchResults.length - 1)); }
                  else if (e.key === 'ArrowUp') { e.preventDefault(); setUserActiveIndex(i => Math.max(i - 1, 0)); }
                  else if (e.key === 'Enter' && userActiveIndex >= 0 && userSearchResults[userActiveIndex]) {
                    e.preventDefault();
                    const u = userSearchResults[userActiveIndex];
                    if (u.email !== email) onSave(u);
                    setIsPopoverOpen(false);
                  }
                  else if (e.key === 'Escape') { onCancel(); }
                }}
                role="combobox"
                aria-expanded={userSearchResults.length > 0}
                aria-controls="grid-user-listbox-inline"
                aria-activedescendant={userActiveIndex >= 0 ? `grid-user-inline-${userActiveIndex}` : undefined}
              />
            </div>
            <div className="max-h-48 overflow-y-auto" role="listbox" id="grid-user-listbox-inline">
              {isSearchingUsers && (
                <div className="flex items-center gap-2 px-3 py-3 text-gray-400 text-sm">
                  <Loader2 size={14} className="animate-spin" /> {t('searchingUsers') || 'Searching...'}
                </div>
              )}
              {!isSearchingUsers && !search && (
                <div className="px-3 py-3 text-gray-400 text-sm">{t('typeToSearchUsers') || 'Type to search users...'}</div>
              )}
              {!isSearchingUsers && search.trim().length >= 2 && userSearchResults.length === 0 && (
                <div className="px-3 py-3 text-gray-400 text-sm">{t('noUsersFound') || 'No users found'}</div>
              )}
              {!isSearchingUsers && search.trim().length > 0 && search.trim().length < 2 && (
                <div className="px-3 py-3 text-gray-400 text-sm">{t('typeAtLeastTwoChars') || 'Type at least 2 characters...'}</div>
              )}
              {userSearchResults.map((u, idx) => {
                const label = u.fullName ? toTitleCase(u.fullName) : u.email;
                const isActive = idx === userActiveIndex;
                return (
                  <div
                    key={u.email + '-' + idx}
                    id={`grid-user-inline-${idx}`}
                    role="option"
                    aria-selected={isActive}
                    className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors ${isActive ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                    title={u.email}
                    onMouseDown={e => {
                      e.preventDefault();
                      if (u.email !== email) {
                        onSave(u);
                      }
                      setIsPopoverOpen(false);
                    }}
                    onMouseEnter={() => setUserActiveIndex(idx)}
                  >
                    <Avatar className="h-7 w-7 flex-shrink-0">
                      <AvatarFallback bgColor={getAvatarColor(label)} initials={getInitials(label)} />
                    </Avatar>
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm font-medium text-gray-900 truncate">{label}</span>
                      <span className="text-xs text-gray-400 truncate">{u.email}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </span>
      );
    }
    const tooltip = email ? email : undefined;
    return (
      <>
        <span
          ref={cellRef}
          className={`flex items-center gap-2 px-2 py-1 rounded justify-between${isFieldEditable ? ' cursor-pointer hover:bg-gray-50' : ''}`}
          title={tooltip}
          onClick={isFieldEditable ? () => setIsPopoverOpen(prev => !prev) : undefined}
          onKeyDown={isFieldEditable ? (e) => {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
              if (e.key === ' ' || e.key === 'Spacebar') e.preventDefault();
              setIsPopoverOpen(true);
            }
          } : undefined}
          tabIndex={isFieldEditable ? 0 : undefined}
          role={isFieldEditable ? "button" : undefined}
          style={{ minHeight: 32 }}
        >
          <span className="flex items-center gap-2 min-w-0">
            <span className="relative flex-shrink-0">
              <Avatar className="h-8 w-8">
                <AvatarFallback bgColor={getAvatarColor(displayName || 'Unassigned')} initials={getInitials(displayName || 'Unassigned')} />
              </Avatar>
              <span className="absolute inset-0 rounded-full bg-black/0 group-hover/link:bg-black/30 transition-colors pointer-events-none" />
            </span>
            <span className="truncate">{displayName || <span className="text-gray-400">Unassigned</span>}</span>
          </span>
          {isFieldEditable && <ChevronDown className="ml-2 w-4 h-4 text-gray-400 flex-shrink-0" />}
        </span>
        {isPopoverOpen && popoverPos &&
          createPortal(
            <div
              ref={popoverRef}
              className="absolute z-[2000] bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden"
              style={{
                top: popoverPos.top + 4,
                left: popoverPos.left,
                width: Math.max(popoverPos.width, 260),
              }}
            >
              <div className="p-2 border-b border-gray-100">
                <input
                  aria-label={t('searchUsersPlaceholder') || 'Search users'}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:border-gray-300 placeholder:text-muted-foreground"
                  placeholder={t('searchUsersPlaceholder') || 'Search users...'}
                  value={search}
                  onChange={e => { handleUserSearch(e.target.value); setUserActiveIndex(-1); }}
                  autoFocus
                  onKeyDown={e => {
                    if (e.key === 'ArrowDown') { e.preventDefault(); setUserActiveIndex(i => Math.min(i + 1, userSearchResults.length - 1)); }
                    else if (e.key === 'ArrowUp') { e.preventDefault(); setUserActiveIndex(i => Math.max(i - 1, 0)); }
                    else if (e.key === 'Enter' && userActiveIndex >= 0 && userSearchResults[userActiveIndex]) {
                      e.preventDefault();
                      const u = userSearchResults[userActiveIndex];
                      if (u.email !== email) onSave(u);
                      setIsPopoverOpen(false);
                    }
                    else if (e.key === 'Escape') { setIsPopoverOpen(false); }
                  }}
                  role="combobox"
                  aria-expanded={userSearchResults.length > 0}
                  aria-controls="grid-user-listbox-popover"
                  aria-activedescendant={userActiveIndex >= 0 ? `grid-user-popover-${userActiveIndex}` : undefined}
                />
              </div>
              <div className="max-h-52 overflow-y-auto" role="listbox" id="grid-user-listbox-popover">
                {isSearchingUsers && (
                  <div className="flex items-center gap-2 px-3 py-3 text-gray-400 text-sm">
                    <Loader2 size={14} className="animate-spin" /> {t('searchingUsers') || 'Searching...'}
                  </div>
                )}
                {!isSearchingUsers && !search && (
                  <div className="px-3 py-3 text-gray-400 text-sm">{t('typeToSearchUsers') || 'Type to search users...'}</div>
                )}
                {!isSearchingUsers && search.trim().length >= 2 && userSearchResults.length === 0 && (
                  <div className="px-3 py-3 text-gray-400 text-sm">{t('noUsersFound') || 'No users found'}</div>
                )}
                {!isSearchingUsers && search.trim().length > 0 && search.trim().length < 2 && (
                  <div className="px-3 py-3 text-gray-400 text-sm">{t('typeAtLeastTwoChars') || 'Type at least 2 characters...'}</div>
                )}
                {userSearchResults.map((u, idx) => {
                  const label = u.fullName ? toTitleCase(u.fullName) : u.email;
                  const isActive = idx === userActiveIndex;
                  return (
                    <div
                      key={u.email + '-' + idx}
                      id={`grid-user-popover-${idx}`}
                      role="option"
                      aria-selected={isActive}
                      className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors ${isActive ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                      title={u.email}
                      onMouseDown={e => {
                        e.preventDefault();
                        if (u.email !== email) {
                          onSave(u);
                        }
                        setIsPopoverOpen(false);
                      }}
                      onMouseEnter={() => setUserActiveIndex(idx)}
                    >
                      <Avatar className="h-7 w-7 flex-shrink-0">
                        <AvatarFallback bgColor={getAvatarColor(label)} initials={getInitials(label)} />
                      </Avatar>
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-medium text-gray-900 truncate">{label}</span>
                        <span className="text-xs text-gray-400 truncate">{u.email}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>,
            document.body
          )}
      </>
    );
  }

  // Special handling for the Client column. Same shape as AssignedToUser:
  // a debounced search popover, but backed by the org-search endpoint and
  // serialized as a structured ClientDto-shaped value rather than a user.
  // Detection is property-based as well as name-based so legacy rows that
  // still hold a flat ClientOrganization string fall through to the regular
  // text cell. Only object values with an fboId-like property qualify.
  // Tightened detection: only match objects that carry a real FBO client
  // identifier. The previous "name or fboId" predicate was too generous —
  // any random object with a "name" property (e.g. an upstream lookup
  // shape, a TextList option object) would get routed into the client
  // editor and crash on save because the commit payload requires fboId.
  // Requiring fboId (or the PascalCase variant) keeps the property-shape
  // path scoped to actual ClientDto values. The columnCode check below
  // remains the primary trigger; this shape sniff is the safety net for
  // rows where the column code is some alias.
  const looksLikeClientValue =
    typeof value === 'object' &&
    value &&
    !Array.isArray(value) &&
    (value.fboId !== undefined || value.FboId !== undefined);
  if (fieldName.toLowerCase() === 'client' || isWorkerOrgField || looksLikeClientValue) {
    const clientObj = (value && typeof value === 'object') ? value : null;
    const clientName: string =
      clientObj?.name ?? clientObj?.Name ?? (typeof value === 'string' ? value : '') ?? '';
    const clientFboId: number | null =
      clientObj?.fboId ?? clientObj?.FboId ?? null;

    // Closure used by both render paths — keeps "is this a real change?"
    // logic in one place so we don't fire a no-op update when the user
    // re-selects the same client.
    const commitClient = (hit: ClientSearchHit) => {
      if (hit.fboId === clientFboId) return;
      onSave({ fboId: hit.fboId, name: hit.name, obCode: hit.obCode });
    };

    if (isEditing) {
      return (
        <span ref={cellRef} className="relative block w-full" style={{ minHeight: 40 }}>
          <div
            className="absolute left-0 top-0 z-[2002] bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden w-full"
            style={{ minWidth: 240 }}
          >
            {/* Show current selection at the top so the user remembers what
                they're replacing — same pattern as the user picker. */}
            {clientName && (
              <div className="flex items-center gap-2.5 px-3 py-2 border-b border-gray-100 bg-gray-50/80">
                <Building2 className="h-5 w-5 text-gray-500 flex-shrink-0" />
                <span className="text-sm font-medium text-gray-700 truncate">{clientName}</span>
              </div>
            )}
            <div className="p-2 border-b border-gray-100">
              <input
                aria-label={isWorkerOrgField ? 'Search by organization name...' : (t('vmsSearchClient') || 'Search by client name...')}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:border-gray-300 placeholder:text-muted-foreground"
                placeholder={isWorkerOrgField ? 'Search by organization name...' : (t('vmsSearchClient') || 'Search by client name...')}
                value={search}
                onChange={e => { handleClientSearch(e.target.value); setClientActiveIndex(-1); }}
                autoFocus
                onBlur={onCancel}
                onKeyDown={e => {
                  if (e.key === 'ArrowDown') { e.preventDefault(); setClientActiveIndex(i => Math.min(i + 1, clientSearchResults.length - 1)); }
                  else if (e.key === 'ArrowUp') { e.preventDefault(); setClientActiveIndex(i => Math.max(i - 1, 0)); }
                  else if (e.key === 'Enter' && clientActiveIndex >= 0 && clientSearchResults[clientActiveIndex]) {
                    e.preventDefault();
                    commitClient(clientSearchResults[clientActiveIndex]);
                    setIsPopoverOpen(false);
                  }
                  else if (e.key === 'Escape') { onCancel(); }
                }}
                role="combobox"
                aria-expanded={clientSearchResults.length > 0}
                aria-controls="grid-client-listbox-inline"
                aria-activedescendant={clientActiveIndex >= 0 ? `grid-client-inline-${clientActiveIndex}` : undefined}
              />
            </div>
            <div className="max-h-48 overflow-y-auto" role="listbox" id="grid-client-listbox-inline">
              {isSearchingClients && (
                <div className="flex items-center gap-2 px-3 py-3 text-gray-400 text-sm">
                  <Loader2 size={14} className="animate-spin" /> {t('searchingUsers') || 'Searching...'}
                </div>
              )}
              {!isSearchingClients && !search && (
                <div className="px-3 py-3 text-gray-400 text-sm">{t('vmsSearchClient') || 'Type to search clients...'}</div>
              )}
              {!isSearchingClients && search.trim().length >= 2 && clientSearchResults.length === 0 && (
                <div className="px-3 py-3 text-gray-400 text-sm">{t('noOptionsAvailable') || 'No clients found'}</div>
              )}
              {!isSearchingClients && search.trim().length > 0 && search.trim().length < 2 && (
                <div className="px-3 py-3 text-gray-400 text-sm">{t('typeAtLeastTwoChars') || 'Type at least 2 characters...'}</div>
              )}
              {clientSearchResults.map((hit, idx) => {
                const isActive = idx === clientActiveIndex;
                return (
                  <div
                    key={`${hit.fboId}-${idx}`}
                    id={`grid-client-inline-${idx}`}
                    role="option"
                    aria-selected={isActive}
                    className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors ${isActive ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                    title={hit.obCode || hit.name}
                    onMouseDown={e => {
                      e.preventDefault();
                      commitClient(hit);
                      setIsPopoverOpen(false);
                    }}
                    onMouseEnter={() => setClientActiveIndex(idx)}
                  >
                    <Building2 className="h-5 w-5 text-gray-500 flex-shrink-0" />
                    <div className="flex flex-col min-w-0">
                      <span className="text-sm font-medium text-gray-900 truncate">{hit.name}</span>
                      {hit.obCode && (
                        <span className="text-xs text-gray-400 truncate">{hit.obCode}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </span>
      );
    }

    // View mode — same shape as AssignedToUser's view chip, but with an
    // org icon and the obCode tucked into the tooltip.
    const displayLabel = clientName || '';
    const tooltip = clientFboId
      ? `${clientName}${clientObj?.obCode ?? clientObj?.ObCode ? ` (${clientObj?.obCode ?? clientObj?.ObCode})` : ''}`
      : clientName;

    return (
      <>
        <span
          ref={cellRef}
          className={`flex items-center gap-2 px-2 py-1 rounded justify-between${isFieldEditable ? ' cursor-pointer hover:bg-gray-50' : ''}`}
          title={tooltip || undefined}
          onClick={isFieldEditable ? () => setIsPopoverOpen(prev => !prev) : undefined}
          onKeyDown={isFieldEditable ? (e) => {
            if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
              if (e.key === ' ' || e.key === 'Spacebar') e.preventDefault();
              setIsPopoverOpen(true);
            }
          } : undefined}
          tabIndex={isFieldEditable ? 0 : undefined}
          role={isFieldEditable ? "button" : undefined}
          style={{ minHeight: 32 }}
        >
          <span className="flex items-center gap-2 min-w-0">
            <Building2 className="h-5 w-5 text-gray-500 flex-shrink-0" />
            <span className="truncate">
              {displayLabel || <span className="text-gray-400">{t('selectOptions') || 'Select client...'}</span>}
            </span>
          </span>
          {isFieldEditable && <ChevronDown className="ml-2 w-4 h-4 text-gray-400 flex-shrink-0" />}
        </span>
        {isPopoverOpen && popoverPos &&
          createPortal(
            <div
              ref={popoverRef}
              className="absolute z-[2000] bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden"
              style={{
                top: popoverPos.top + 4,
                left: popoverPos.left,
                width: Math.max(popoverPos.width, 280),
              }}
            >
              <div className="p-2 border-b border-gray-100">
                <input
                  aria-label={t('vmsSearchClient') || 'Search by client name...'}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:border-gray-300 placeholder:text-muted-foreground"
                  placeholder={t('vmsSearchClient') || 'Search by client name...'}
                  value={search}
                  onChange={e => { handleClientSearch(e.target.value); setClientActiveIndex(-1); }}
                  autoFocus
                  onKeyDown={e => {
                    if (e.key === 'ArrowDown') { e.preventDefault(); setClientActiveIndex(i => Math.min(i + 1, clientSearchResults.length - 1)); }
                    else if (e.key === 'ArrowUp') { e.preventDefault(); setClientActiveIndex(i => Math.max(i - 1, 0)); }
                    else if (e.key === 'Enter' && clientActiveIndex >= 0 && clientSearchResults[clientActiveIndex]) {
                      e.preventDefault();
                      commitClient(clientSearchResults[clientActiveIndex]);
                      setIsPopoverOpen(false);
                    }
                    else if (e.key === 'Escape') { setIsPopoverOpen(false); }
                  }}
                  role="combobox"
                  aria-expanded={clientSearchResults.length > 0}
                  aria-controls="grid-client-listbox-popover"
                  aria-activedescendant={clientActiveIndex >= 0 ? `grid-client-popover-${clientActiveIndex}` : undefined}
                />
              </div>
              <div className="max-h-52 overflow-y-auto" role="listbox" id="grid-client-listbox-popover">
                {isSearchingClients && (
                  <div className="flex items-center gap-2 px-3 py-3 text-gray-400 text-sm">
                    <Loader2 size={14} className="animate-spin" /> {t('searchingUsers') || 'Searching...'}
                  </div>
                )}
                {!isSearchingClients && !search && (
                  <div className="px-3 py-3 text-gray-400 text-sm">{t('vmsSearchClient') || 'Type to search clients...'}</div>
                )}
                {!isSearchingClients && search.trim().length >= 2 && clientSearchResults.length === 0 && (
                  <div className="px-3 py-3 text-gray-400 text-sm">{t('noOptionsAvailable') || 'No clients found'}</div>
                )}
                {!isSearchingClients && search.trim().length > 0 && search.trim().length < 2 && (
                  <div className="px-3 py-3 text-gray-400 text-sm">{t('typeAtLeastTwoChars') || 'Type at least 2 characters...'}</div>
                )}
                {clientSearchResults.map((hit, idx) => {
                  const isActive = idx === clientActiveIndex;
                  return (
                    <div
                      key={`${hit.fboId}-${idx}`}
                      id={`grid-client-popover-${idx}`}
                      role="option"
                      aria-selected={isActive}
                      className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors ${isActive ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                      title={hit.obCode || hit.name}
                      onMouseDown={e => {
                        e.preventDefault();
                        commitClient(hit);
                        setIsPopoverOpen(false);
                      }}
                      onMouseEnter={() => setClientActiveIndex(idx)}
                    >
                      <Building2 className="h-5 w-5 text-gray-500 flex-shrink-0" />
                      <div className="flex flex-col min-w-0">
                        <span className="text-sm font-medium text-gray-900 truncate">{hit.name}</span>
                        {hit.obCode && (
                          <span className="text-xs text-gray-400 truncate">{hit.obCode}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>,
            document.body
          )}
      </>
    );
  }

  // Handle TextList with or without allowMultipleSelections
  if (column.columnTypeCode === "TextList") {
    if (column.allowMultipleSelections) {
      // Value is expected to be an array of codes or objects
      const valueArray = Array.isArray(value) ? value : (typeof value === 'string' && value ? [value] : []);
      const options = (column.customValues || []).map(opt => ({
        value: String(opt.code),
        label: getLocalizedField(opt, 'title', language),
        color: opt.color || undefined,
      }));

      if (isEditing) {
        return (
          <MultiSelectCell
            value={valueArray}
            options={options}
            column={column}
            rowId={String(order.id ?? "")}
            onSave={onSave}
            onCancel={onCancel}
          />
        );
      } else {
        let display = "";
        if (typeof column.subColumnName === 'string' && column.subColumnName) {
          // Join subColumnName from each object in the array (case-insensitive)
          display = valueArray
            .map(item => {
              if (item && typeof item === 'object') {
                const key = Object.keys(item).find(k => k.toLowerCase() === column.subColumnName!.toLowerCase());
                return key ? item[key] : "";
              }
              return String(item);
            })
            .filter(Boolean)
            .join(", ");
        } else {
          // Map codes to their translated labels, then join
          display = valueArray.map(item => {
            const code = String(item);
            
            // If column has codeValueSource, use code values from the service
            if (column.codeValueSource && codeValues.isReady) {
              try {
                const displayText = codeValues.getCodeValueTextByCode(code, column.codeValueSource);
                if (displayText) {
                  return displayText;
                }
              } catch (error) {
                console.warn('Failed to get code value text for TextList:', error);
              }
            }
            
            // Otherwise, use customValues
            const match = column.customValues?.find(opt => opt.code === code);
            return match ? getLocalizedField(match, 'title', language) : code;
          }).join(", ");
        }
        return (
          isFieldEditable ?
            (<div className="block text-left px-2 py-1 cursor-pointer" onClick={onEdit} title={t('clickToEdit')}>
              {display || (<span className="text-gray-400">{t('selectOptions')}</span>)}
            </div>)
          :
            (<div className="block text-left px-2 py-1">
              {display}
            </div>)
        );
      }
    } else {
      // TextList without allowMultipleSelections - handle comma-separated codes
      if (value && typeof value === 'string' && (column.customValues || column.codeValueSource)) {
        const codes = value.split(',').map(code => code.trim());
        const displayTexts = codes.map(code => {
          // If column has codeValueSource, use code values from the service
          if (column.codeValueSource && codeValues.isReady) {
            try {
              const displayText = codeValues.getCodeValueTextByCode(code, column.codeValueSource);
              if (displayText) {
                return displayText;
              }
            } catch (error) {
              console.warn('Failed to get code value text for TextList:', error);
            }
          }
          
          // Otherwise, use customValues
          const match = column.customValues?.find((opt: any) => opt.code === code);
          return match ? getLocalizedField(match, 'title', language) : code;
        });
        const display = displayTexts.join(', ');
        
        return isFieldEditable ? (
          <div className="block text-left px-2 py-1 cursor-pointer" onClick={onEdit} title={t('clickToEdit')}>
            {display}
          </div>
        ) : (
          <div className="block text-left px-2 py-1">
            {display}
          </div>
        );
      }
    }
  }

  if (isEditing) {
    return <EditableCell value={String(value ?? "")} column={column} rowId={String(order.id ?? "")} onSave={onSave} onCancel={onCancel} />;
  }

  // Dropdowns for columns with options
  if (column.customValues && column.customValues.length > 0) {
    if (isFieldEditable) {
      return (
        <div className="block text-left px-2 py-1">
          <DropdownCell
            value={String(value ?? "")}
            options={column.customValues.map(opt => ({ 
              value: getLocalizedField(opt, 'title', language),
              label: getLocalizedField(opt, 'title', language),
              color: String(opt.color ?? "") 
            }))}
            customValues={column.customValues}
            rowId={String(order.id ?? "")}
            column={column}
            onSave={onSave}
          />
        </div>
      );
    } else {
      // Not editable: show value as plain text, with color if defined.
      // Same reason as the cell-background lookup in workflow-grid.tsx: `false` and `0` are real
      // values with their own colour, so this cannot be a truthiness check.
      const hasValue = value !== null && value !== undefined && value !== '';
      const match = hasValue ? column.customValues.find(opt => opt.code === String(value)) : undefined;
      const display = match ? getLocalizedField(match, 'title', language) : value;
      const color = match && match.color ? match.color : undefined;
          return (
      <div className="block text-left px-2 py-1 truncate" style={color ? { backgroundColor: color, borderRadius: 8, textAlign: 'center' } : {}} title={display}>
        {display}
      </div>
    );
    }
  }

  // Date — match BOTH "date" and "datetime" columnTypeCode values. The
  // seeded VMS StartDate uses "Date" while custom-fields-modal-created
  // columns may use "DateTime"; both are date-shaped values that need the
  // formatted render path. Previously only "datetime" was matched, so "Date"
  // columns silently fell through to the catch-all text renderer and
  // displayed the raw ISO string regardless of the user's fieldFormatting
  // ("Short date") choice in the field editor.
  if (column.customValuesType?.toLowerCase() === "date"
      || column.columnTypeCode?.toLowerCase() === "datetime"
      || column.columnTypeCode?.toLowerCase() === "date") {
    if (isEditing) {
      return <EditableCell value={value} column={column} rowId={String(order.id ?? "")} onSave={onSave} onCancel={onCancel} />;
    }
    
    // Determine format based on fieldFormatting (default to ShortDate)
    const formatting = column.fieldFormatting || "ShortDate";
    const formattedValue = formatting === "DateTime" 
      ? formatDateTime(value, language)
      : formatDateOnly(value, language);
    
    // Not editing: show formatted date only
    if (!isFieldEditable) {
      return value ? (
        <span className="block text-center px-2 py-1 truncate" title={formattedValue}>
          {formattedValue}
        </span>
      ) : null;
    }
    // Editable but not in edit mode: apply formatting based on fieldFormatting
    const editableFormatting = column.fieldFormatting || "ShortDate";
    const displayValue = value 
      ? (editableFormatting === "DateTime" ? formatDateTime(value, language) : formatDateOnly(value, language))
      : t('selectDate');
    const titleValue = value
      ? (editableFormatting === "DateTime" ? formatDateTime(value, language) : formatDateOnly(value, language))
      : t('selectDate');
    
    return (
      <div className="cursor-pointer hover:bg-gray-50 rounded" onClick={onEdit} title={t('clickToEdit')}>
        <div className="flex items-center border border-gray-300 rounded px-3 py-2 min-w-[180px] bg-white">
          <span className="mr-2 text-gray-500 flex-shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor"><rect x="3" y="4" width="18" height="18" rx="2" strokeWidth="2" stroke="currentColor" fill="none"/><path d="M16 2v4M8 2v4M3 10h18" strokeWidth="2" stroke="currentColor" fill="none"/></svg>
          </span>
          <span className={`truncate ${value ? "text-black" : "text-gray-400"}`} title={titleValue}>
            {displayValue}
          </span>
        </div>
      </div>
    );
  }

  // Collection type (generic for arrays of complex objects)
  if (isCollectionField(column)) {
    const items = Array.isArray(value) ? value : [];
    
    // Get field interaction from stage config
    const fieldInteraction = stageConfig?.fieldInteractions?.find(
      (fi: any) => fi.fieldConfigurationId === column.fieldConfigurationId
    );
    
    // Generic collection rendering
    return (
      <CollectionCell
        value={items}
        column={column}
        fieldInteraction={fieldInteraction}
      />
    );
  }

  // Request Progress — composite column rendering progress bar + task name
  if (column.columnTypeCode === "RequestProgress") {
    return <RequestProgressCell order={order} />
  }

  // Number / Double — honors column.fieldFormatting = "percent" by rendering the
  // value as a percentage (0.975 → "97.5%"). Backend stores the raw 0.0–1.0 decimal
  // (LLM confidence convention) and tags the field with FieldFormatting=percent in
  // the FieldConfiguration; this branch is the only place the rendering layer
  // resolves that hint. Falls through to the default text path when the value is
  // null / undefined / NaN so the cell stays empty rather than showing "NaN%".
  const numericTypes = ["double", "number", "decimal", "float", "int", "integer"];
  if (numericTypes.includes(column.columnTypeCode?.toLowerCase() ?? "")) {
    const numeric = typeof value === "number" ? value : (value == null ? null : Number(value));
    if (numeric == null || Number.isNaN(numeric)) {
      // Falls through to the default text renderer for null / unparseable values.
    } else if (column.fieldFormatting?.toLowerCase() === "percent") {
      // One decimal — matches the typical confidence display ("97.5%" rather than
      // "97%" or "97.50%"). If a column ever wants a different precision we'd
      // extend fieldFormatting to something like "percent:0" / "percent:2".
      const pct = (numeric * 100).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
      return (
        <span
          className={`block text-right px-2 py-1 truncate${isFieldEditable ? ' cursor-pointer hover:bg-gray-50 rounded' : ''}`}
          onClick={isFieldEditable ? onEdit : undefined}
          title={isFieldEditable ? t('clickToEdit') : `${pct}%`}
        >
          {pct}%
        </span>
      );
    } else {
      // Plain number — rendered AS-IS by default (no thousands grouping), so ids
      // like AtsPlacementId show "147926", not "147,926". Opt into locale grouping
      // (the "1,234,567" form) by tagging the FieldConfiguration with
      // FieldFormatting="thousands". Currency columns come through as text with
      // their own formatter; a future true-currency type would land here with
      // something like FieldFormatting="currency:CAD".
      const useThousands = column.fieldFormatting?.toLowerCase() === "thousands";
      const formatted = useThousands ? numeric.toLocaleString() : String(numeric);
      return (
        <span
          className={`block text-right px-2 py-1 truncate${isFieldEditable ? ' cursor-pointer hover:bg-gray-50 rounded' : ''}`}
          onClick={isFieldEditable ? onEdit : undefined}
          title={isFieldEditable ? t('clickToEdit') : formatted}
        >
          {formatted}
        </span>
      );
    }
  }

  // Boolean
  if (column.columnTypeCode.toLowerCase() === "boolean" || column.customValuesType === "YesNo" || column.customValuesType === "boolean") {
    return (
      <div className="block text-left px-2 py-1 truncate">
        <BooleanCell 
          value={value} 
          rowId={String(order.id ?? "")} 
          onSave={onSave}
          isEditable={isFieldEditable}
        />
      </div>
    );
  }

  // Checklist column type
  if (column.columnTypeCode?.toLowerCase() === 'checklist') {
    let completedCount = 0;
    let totalCount = 0;
    
    try {
      let items: Array<{ isCompleted: boolean; isDeleted: boolean }> = [];
      
      if (Array.isArray(value)) {
        // Backend returns array directly
        items = value;
      } else if (value && typeof value === 'object' && 'items' in value) {
        // Legacy format with items property
        items = (value as any).items || [];
      } else if (value && typeof value === 'string') {
        const parsed = JSON.parse(value);
        items = Array.isArray(parsed) ? parsed : (parsed.items || []);
      }
      
      // Filter out deleted items for display
      const activeItems = items.filter(item => !item.isDeleted);
      totalCount = activeItems.length;
      completedCount = activeItems.filter(item => item.isCompleted).length;
    } catch (error) {
      console.warn('Failed to parse checklist data:', error);
    }
    
    return (
      <div 
        className="block text-center px-2 py-1 cursor-pointer hover:bg-blue-50 rounded font-medium text-blue-600"
        onClick={onEdit}
        title="Click to open checklist"
      >
        {totalCount > 0 ? `${completedCount}/${totalCount}` : '0/0'}
      </div>
    );
  }

  // Default: text
  // If this is a FullName field, render avatar + name. When the field is
  // marked editable in the field config, wire the same click-to-edit
  // affordance the plain-text branch below uses — otherwise the avatar
  // wrapper swallows the cell-edit gesture and the column appears stuck
  // read-only (regression seen after renaming the VMS WorkerName column
  // code to WorkerFullName, which made this branch match unexpectedly).
  if ((fieldName.toLowerCase().includes("fullname") || column.columnNameEnglish?.toLowerCase().includes("fullname")) && value) {
    if (isFieldEditable) {
      return (
        <div
          className="flex items-center gap-2 px-2 py-1 cursor-pointer hover:bg-gray-50 rounded"
          onClick={onEdit}
          title={t('clickToEdit')}
        >
          <Avatar className="h-8 w-8 flex-shrink-0">
            <AvatarFallback bgColor={getAvatarColor(String(value))} initials={getInitials(String(value))} />
          </Avatar>
          <span className="truncate">{String(value)}</span>
        </div>
      );
    }
    return (
      <span className="flex items-center gap-2 px-2 py-1">
        <Avatar className="h-8 w-8 flex-shrink-0">
          <AvatarFallback bgColor={getAvatarColor(String(value))} initials={getInitials(String(value))} />
        </Avatar>
        <span className="truncate" title={String(value)}>{String(value)}</span>
      </span>
    );
  }

  // Resolve optional badge from a sibling column's field configuration.
  // Must run before any early return (e.g. codeValueSource) so the badge is
  // available for all code paths. Use == null so valid falsy values like 0 are kept.
  const badgeLabel = (() => {
    if (!column.badgeColumnCode) return null;
    const rawBadge = getCellValue(order, column.badgeColumnCode);
    if (rawBadge == null) return null;
    const badgeFieldConfig = state.configuration?.fieldsInUse
      .find(f => f.columnCode === column.badgeColumnCode);
    const match = badgeFieldConfig?.customValues
      ?.find(v => v.code === String(rawBadge));
    return match ? getLocalizedField(match, 'title', language) : String(rawBadge);
  })();

  // Handle CodeValueSource columns - this should come before the default text display
  if (column.codeValueSource && codeValues.isReady) {
    const displayValue = getDisplayValueForCodeValueSource(value, column);
    if (isFieldEditable) {
      return (
        <div className="flex items-center gap-1 text-left px-2 py-1 cursor-pointer hover:bg-gray-50 rounded" onClick={onEdit} title={t('clickToEdit')}>
          <span className="truncate">{displayValue ?? ""}</span>
          {badgeLabel && (
            <span className="inline-flex shrink-0 items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
              {badgeLabel}
            </span>
          )}
        </div>
      );
    }
    return (
      <div className="flex items-center gap-1 text-left px-2 py-1" title={displayValue ?? ""}>
        <span className="truncate">{displayValue ?? ""}</span>
        {badgeLabel && (
          <span className="inline-flex shrink-0 items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
            {badgeLabel}
          </span>
        )}
      </div>
    );
  }

  // Email columns: compose + copy. Must sit BEFORE the generic text fallback below — email columns
  // previously fell straight through to it and rendered as plain text. Covers both populations:
  // system columns typed 'Email', and custom fields whose input type is Email (stored in a Text
  // slot). AssignedToUser is excluded inside isEmailField — it is typed Email on the request configs
  // but is the user picker, and its value is an object.
  if (isEmailField(column)) {
    return (
      <EmailCell
        value={getDisplayValueFromColumn(value, column)}
        isEditable={!!isFieldEditable}
        onEdit={onEdit}
        badgeLabel={badgeLabel}
      />
    );
  }

  // Default text display: allow inline edit if isFieldEditable
  if (isFieldEditable) {
    const displayText = getDisplayValueFromColumn(value, column);
    return (
      <div className="flex items-center gap-1 text-left px-2 py-1 cursor-pointer hover:bg-gray-50 rounded" onClick={onEdit} title={t('clickToEdit')}>
        <span className="truncate">{displayText}</span>
        {badgeLabel && (
          <span className="inline-flex shrink-0 items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
            {badgeLabel}
          </span>
        )}
      </div>
    );
  }
  
  
  // Final fallback - ensure we never render objects directly
  let displayText = value;
  if (typeof value === 'object' && value !== null) {
    if (Array.isArray(value)) {
      // If it's an array that got here, join codes or convert to string
      displayText = value.map(v => {
        if (typeof v === 'object' && v !== null) {
          return v.code || v.Code || JSON.stringify(v);
        }
        return String(v);
      }).join(', ');
    } else {
      displayText = getDisplayValueFromColumn(value, column);
    }
  }
  
  return (
    <div className="flex items-center gap-1 text-left px-2 py-1" title={String(displayText)}>
      <span className="truncate">{displayText}</span>
      {badgeLabel && (
        <span className="inline-flex shrink-0 items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
          {badgeLabel}
        </span>
      )}
    </div>
  );
}
